import axiosInstance from "./axios";

export interface SessionConfig {
  interval_seconds: number;
  timeout_minutes: number;
  endpoint: string;
}

export interface SessionStats {
  total_sessions: number;
  total_time_seconds: number;
  total_time_formatted: string;
  average_session_seconds: number;
  average_session_formatted: string;
  total_actions: number;
  most_visited_pages: Record<string, number>;
}

export interface ActiveSession {
  id: number;
  session_id: string;
  user_name?: string;
  user_email?: string;
  started_at: string;
  last_activity_at: string;
  duration_minutes: number;
  actions_count: number;
  ip_address: string;
  pages_count: number;
}

class SessionTrackingService {
  private config: SessionConfig | null = null;
  private heartbeatInterval: any = null;
  private isPageVisible = true;
  private lastActivity = Date.now();
  private currentPage = "";

  // === ОПТИМИЗАЦИЯ #6: Кэширование Session Config с защитой от race condition ===
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;
  private listenersSetup = false; // Флаг для предотвращения дублирования event listeners

  /**
   * Инициализация сервиса отслеживания сессий
   */
  async init(): Promise<void> {
    // Если уже инициализирован, не делаем повторный запрос
    if (this.isInitialized && this.config) {
      console.log("✅ Session tracking already initialized, skipping");
      return;
    }

    // Если инициализация уже идёт, ждем её завершения (защита от race condition)
    if (this.initPromise) {
      console.log("⏳ Waiting for existing session tracking init...");
      return this.initPromise;
    }

    // Создаём новый промис инициализации
    console.log("🔄 Initializing session tracking...");
    this.initPromise = (async () => {
      try {
        // Получаем конфигурацию с сервера
        const response = await axiosInstance.get("/api/session/config");
        this.config = response.data;
        this.isInitialized = true;

        // Устанавливаем обработчики событий (только один раз)
        if (!this.listenersSetup) {
          this.setupEventListeners();
          this.listenersSetup = true;
        }

        // Запускаем heartbeat
        this.startHeartbeat();

        // Отслеживаем первое посещение страницы
        this.trackPageVisit(window.location.pathname);

        console.log("✅ Session tracking initialized successfully");
      } catch (error) {
        console.error("Failed to initialize session tracking:", error);
        this.isInitialized = false;
      } finally {
        // Сбрасываем промис после завершения
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }

  /**
   * Настройка обработчиков событий
   */
  private setupEventListeners(): void {
    // Page Visibility API - отслеживание переключения вкладок
    document.addEventListener("visibilitychange", () => {
      this.isPageVisible = !document.hidden;

      if (this.isPageVisible) {
        this.updateActivity();
      }
    });

    // Отслеживание активности пользователя
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    activityEvents.forEach((event) => {
      document.addEventListener(
        event,
        () => {
          this.updateActivity();
        },
        { passive: true },
      );
    });

    // Отслеживание изменений URL (для SPA)
    const self = this;
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      // Проверяем изменение страницы асинхронно
      setTimeout(() => {
        const newPage = window.location.pathname;
        if (newPage !== self.currentPage) {
          self.trackPageVisit(newPage);
        }
      }, 0);
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      // Проверяем изменение страницы асинхронно
      setTimeout(() => {
        const newPage = window.location.pathname;
        if (newPage !== self.currentPage) {
          self.trackPageVisit(newPage);
        }
      }, 0);
    };

    window.addEventListener("popstate", () => {
      const newPage = window.location.pathname;
      if (newPage !== this.currentPage) {
        this.trackPageVisit(newPage);
      }
    });

    // Не используем beforeunload для завершения сессии:
    // - beforeunload не гарантирует выполнение async запросов
    // - создает race conditions при редиректах (особенно при logout)
    // - сервер автоматически завершает неактивные сессии по таймауту
  }

  /**
   * Обновить время последней активности
   */
  private updateActivity(): void {
    this.lastActivity = Date.now();
  }

  /**
   * Запустить heartbeat
   */
  private startHeartbeat(): void {
    if (!this.config) return;

    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, this.config.interval_seconds * 1000);
  }

  /**
   * Остановить heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Отправить heartbeat на сервер
   */
  private async sendHeartbeat(): Promise<void> {
    // Не отправляем heartbeat если страница скрыта больше 5 минут
    const timeSinceActivity = Date.now() - this.lastActivity;
    const maxInactiveTime = 5 * 60 * 1000; // 5 минут

    if (!this.isPageVisible && timeSinceActivity > maxInactiveTime) {
      return;
    }

    try {
      const response = await axiosInstance.post("/api/session/heartbeat", {
        page: this.currentPage,
        visibility: this.isPageVisible,
        last_activity: this.lastActivity,
      });

      // Если сессия неактивна, пытаемся восстановить
      if (!response.data.session_active) {
        this.init();
      }
    } catch (error) {
      // Heartbeat failed silently
    }
  }

  /**
   * Отследить посещение страницы
   */
  async trackPageVisit(
    page: string,
    title?: string,
    projectId?: number,
  ): Promise<void> {
    // Защита от дублирования одной и той же страницы
    if (this.currentPage === page) {
      return;
    }

    this.currentPage = page;
    this.updateActivity();

    try {
      const data: any = { page };

      if (title) data.title = title;
      if (projectId) data.project_id = projectId;

      await axiosInstance.post("/api/session/page-visit", data);
    } catch (error) {
      // Page visit tracking failed silently
    }
  }

  /**
   * Отследить действие пользователя
   */
  async trackAction(action: string, data?: Record<string, any>): Promise<void> {
    this.updateActivity();

    try {
      await axiosInstance.post("/api/session/track-action", {
        action,
        data: data || {},
      });
    } catch (error) {
      // Action tracking failed silently
    }
  }

  /**
   * Получить статистику сессий пользователя
   */
  async getStats(from?: string, to?: string): Promise<SessionStats> {
    const params: any = {};
    if (from) params.from = from;
    if (to) params.to = to;

    const response = await axiosInstance.get("/api/session/stats", { params });
    return response.data;
  }

  /**
   * Получить глобальную статистику всех пользователей (для админки)
   */
  async getGlobalStats(from?: string, to?: string): Promise<SessionStats> {
    const params: any = {};
    if (from) params.from = from;
    if (to) params.to = to;

    const response = await axiosInstance.get("/api/session/global-stats", {
      params,
    });
    return response.data;
  }

  /**
   * Получить активные сессии текущего пользователя
   */
  async getActiveSessions(): Promise<{
    sessions: ActiveSession[];
    count: number;
  }> {
    const response = await axiosInstance.get("/api/session/active");
    return response.data;
  }

  /**
   * Получить все активные сессии всех пользователей (для админки)
   */
  async getAllActiveSessions(): Promise<{
    sessions: ActiveSession[];
    count: number;
  }> {
    const response = await axiosInstance.get("/api/session/all-active");
    return response.data;
  }

  /**
   * Завершить текущую сессию
   */
  async endSession(): Promise<void> {
    try {
      this.stopHeartbeat();

      // Проверяем есть ли auth токен - если нет, сессия уже завершена
      const hasToken = localStorage.getItem("auth_token");
      if (!hasToken) {
        this.reset();
        return;
      }

      // Пытаемся завершить сессию на сервере
      await axiosInstance.post("/api/session/end");

      // Сбрасываем инициализацию после успешного завершения
      this.reset();
    } catch (error: any) {
      // Игнорируем ошибки которые означают что сессия уже завершена
      if (error.response?.status === 401 || error.response?.status === 419) {
        // 401 = unauthorized (токен невалиден)
        // 419 = CSRF token mismatch (PHP сессия уже инвалидирована)
        this.reset();
        return;
      }
    }
  }

  /**
   * Сбросить состояние инициализации
   * Используется при logout или при завершении сессии
   */
  reset(): void {
    this.isInitialized = false;
    this.initPromise = null;
    this.config = null;
    // НЕ сбрасываем listenersSetup, так как event listeners остаются установленными
    // и не нуждаются в переустановке
    console.log("🧹 Session tracking reset");
  }

  /**
   * Очистить ресурсы при уничтожении
   */
  destroy(): void {
    this.stopHeartbeat();
  }

  /**
   * Получить текущую конфигурацию
   */
  getConfig(): SessionConfig | null {
    return this.config;
  }

  /**
   * Проверить, активно ли отслеживание
   */
  isActive(): boolean {
    return this.heartbeatInterval !== null;
  }
}

// Создаем singleton инстанс
export const sessionTrackingService = new SessionTrackingService();

// Типы для экспорта
export type { SessionConfig, SessionStats, ActiveSession };
export default sessionTrackingService;
